/**
 * Profiles resource — pure async data functions.
 *
 * Hooks/components must not call `supabase.from('profiles')` directly.
 * Add new reads/writes here and consume them from a hook in `src/hooks/`.
 */
import { supabase } from '@/integrations/supabase/client';

export interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  avatar_url: string | null;
  city: string | null;
  state_code: string | null;
  seller_rating: number | null;
  total_sales: number | null;
  verification_tier: string | null;
  specialized_games: string[] | null;
  booth_description: string | null;
  social_links: unknown;
  header_background: string | null;
  header_background_position: number | null;
  // Physical store
  seller_has_physical_store: boolean | null;
  store_name: string | null;
  store_address_line1: string | null;
  store_address_line2: string | null;
  store_city: string | null;
  store_state_code: string | null;
  store_zip: string | null;
  store_latitude: number | null;
  store_longitude: number | null;
  // Selling preferences
  will_ship_items: boolean | null;
  offers_local_pickup: boolean | null;
  accepts_trades: boolean | null;
  // Currently buying
  currently_buying: boolean | null;
  // Contact
  phone: string | null;
  email: string | null;
  // Seller approval (legacy gate removed; only seller_status still used for admin enforcement)
  seller_status: 'pending' | 'approved' | 'rejected' | null;
  // Pickup location
  pickup_address_line1: string | null;
  pickup_address_line2: string | null;
  pickup_city: string | null;
  pickup_state_code: string | null;
  pickup_zip: string | null;
  pickup_latitude: number | null;
  pickup_longitude: number | null;
  last_collectr_sync_at: string | null;
}

const PROFILE_COLUMNS = `
  id, user_id, username, avatar_url, city, state_code, seller_rating, total_sales,
  verification_tier, specialized_games, booth_description, social_links,
  header_background, header_background_position,
  seller_has_physical_store, store_name, store_address_line1, store_address_line2,
  store_city, store_state_code, store_zip, store_latitude, store_longitude,
  will_ship_items, offers_local_pickup, accepts_trades,
  currently_buying,
  phone, email,
  seller_status,
  pickup_address_line1, pickup_address_line2, pickup_city, pickup_state_code, pickup_zip,
  pickup_latitude, pickup_longitude, last_collectr_sync_at
`;

export async function fetchProfileByUserId(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select(PROFILE_COLUMNS)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return (data as Profile | null) ?? null;
}

/**
 * Update arbitrary profile columns for the given user.
 * Callers must ensure the payload matches the profiles schema.
 */
export async function updateProfile(
  userId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update(patch as any)
    .eq('user_id', userId);
  if (error) throw error;
}

export interface ProfileTermsAndVerification {
  general_terms_accepted_at: string | null;
  general_terms_version: string | null;
  terms_accepted_at: string | null;
  terms_version: string | null;
  verification_tier: string | null;
  email_verified: boolean | null;
  identity_verified_at: string | null;
}

export async function fetchProfileTermsAndVerification(
  userId: string,
): Promise<ProfileTermsAndVerification | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'general_terms_accepted_at, general_terms_version, terms_accepted_at, terms_version, verification_tier, email_verified, identity_verified_at',
    )
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as ProfileTermsAndVerification | null) ?? null;
}

export async function fetchProfileHasPassword(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('has_password')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return !!(data as { has_password?: boolean } | null)?.has_password;
}

export interface ShopShippingSettings {
  shop_shipping_flat_cents: number | null;
  shop_shipping_priority_cents: number | null;
  shop_free_shipping_threshold_cents: number | null;
  shop_ships_international: boolean | null;
  shop_handling_days: number | null;
  shop_return_policy_text: string | null;
}

/** Uses `id` (profile PK == auth user id) for parity with existing callers. */
export async function fetchShopShippingSettings(
  profileId: string,
): Promise<ShopShippingSettings | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'shop_shipping_flat_cents, shop_shipping_priority_cents, shop_free_shipping_threshold_cents, shop_ships_international, shop_handling_days, shop_return_policy_text',
    )
    .eq('id', profileId)
    .maybeSingle();
  if (error) throw error;
  return (data as ShopShippingSettings | null) ?? null;
}

export async function updateShopShippingSettings(
  profileId: string,
  patch: Partial<ShopShippingSettings>,
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update(patch as any)
    .eq('id', profileId);
  if (error) throw error;
}
